import { type DateRange } from 'react-day-picker';

import { useDateLocalize } from '@mint-lib/formatting-hooks/locales';
import { useTranslation } from '@mint-lib/i18n';

import { Toggle } from '../../lib/toggle.jsx';
import {
  getDateRangeBySelectButton,
  selectButtons,
  type SelectButtonType,
} from './utils/getDateRangeBySelectButton.js';

type CalendarSidebarProps = Readonly<{
  /**
   * The children to be rendered inside the calendar sidebar
   */
  children: React.ReactNode;
  /**
   * The all time button configuration
   */
  allTimeButton?: { from?: Date; to?: Date };
  /**
   * The selected button
   */
  selectedButton?: SelectButtonType;
  /**
   * The onSelect event handler
   */
  onSelect: (dateRange: DateRange, key?: SelectButtonType) => void;
}>;
function CalendarSidebar({
  children,
  allTimeButton,
  onSelect,
  selectedButton,
}: CalendarSidebarProps) {
  const { t } = useTranslation('@mint-lib/tw-components');
  const localize = useDateLocalize();

  const buttonsLabels: Record<SelectButtonType, string> = {
    yesterday: t('Yesterday'),
    thisWeek: t('This week'),
    lastWeek: t('Last week'),
    thisMonth: t('This month'),
    lastMonth: t('Last month'),
    thisQuarter: t('This quarter'),
    lastQuarter: t('Last quarter'),
    thisYear: t('This Year'),
    lastYear: t('Last Year'),
    allTime: t('All time'),
  };
  return (
    <div className="grid grid-cols-[auto_1fr]" data-testid="calendar-sidebar">
      <div className="grid items-center justify-center gap-2 border-r border-border-secondary p-2">
        {selectButtons.map((key) => {
          if (key === 'allTime' && !allTimeButton) {
            return null;
          }
          return (
            <Toggle
              key={key}
              size="small"
              value={key}
              onClick={() => {
                const { from, to } = getDateRangeBySelectButton(
                  key,
                  localize,
                  allTimeButton,
                );
                onSelect({ from, to }, key);
              }}
              pressed={selectedButton === key}
            >
              {buttonsLabels[key]}
            </Toggle>
          );
        })}
      </div>
      <div className="w-fit min-w-[505px]">{children}</div>
    </div>
  );
}

export default CalendarSidebar;
